import React, { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const IconUpload = ({ 
  currentIconUrl, 
  currentIconAlt = '',
  onUpload, 
  onRemove,
  onAltTextChange,
  isUploading = false 
}) => {
  const { isDarkMode } = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP, or SVG)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    if (onUpload) {
      onUpload(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onRemove) {
      onRemove();
    }
  };

  const displayUrl = previewUrl || currentIconUrl;

  return (
    <div className="card" style={{ 
      backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
      border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
      borderRadius: '8px',
      padding: '1.5rem',
      marginBottom: '1rem'
    }}>
      <h3 style={{ 
        margin: '0 0 1rem 0', 
        color: isDarkMode ? 'white' : '#333',
        fontSize: '1.25rem'
      }}>
        Icon Management
      </h3>

      {/* Current Icon Display */}
      {displayUrl && (
        <div style={{ 
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ 
            display: 'inline-block',
            position: 'relative',
            marginBottom: '1rem'
          }}>
            <img 
              src={displayUrl} 
              alt={currentIconAlt || 'Icon preview'} 
              style={{ 
                width: '128px', 
                height: '128px', 
                borderRadius: '12px',
                objectFit: 'cover',
                border: `2px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }} 
            />
            <button
              className="btn btn-danger"
              onClick={handleRemove}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                padding: '0',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Remove icon"
            >
              ×
            </button>
          </div>
          
          {/* Icon Alt Text */}
          <div className="form-group" style={{ maxWidth: '300px', margin: '0 auto' }}>
            <label className="form-label" htmlFor="iconAlt">
              Icon Alt Text
            </label>
            <input
              type="text"
              id="iconAlt"
              className="form-input"
              value={currentIconAlt}
              onChange={(e) => onAltTextChange && onAltTextChange(e.target.value)}
              placeholder="Descriptive text for accessibility"
              style={{
                backgroundColor: isDarkMode ? '#3a3a3a' : 'white',
                color: isDarkMode ? 'white' : '#333',
                border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                textAlign: 'center'
              }}
            />
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        style={{
          border: `2px dashed ${dragActive ? '#007bff' : isDarkMode ? '#555' : '#ddd'}`,
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: dragActive ? (isDarkMode ? '#3a3a3a' : '#f8f9fa') : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          marginBottom: '1rem'
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        
        <div style={{ 
          color: isDarkMode ? '#ccc' : '#666',
          fontSize: '1.125rem',
          marginBottom: '0.5rem'
        }}>
          {dragActive ? 'Drop the image here' : 'Click to upload or drag and drop'}
        </div>
        
        <div style={{ 
          color: isDarkMode ? '#aaa' : '#888',
          fontSize: '0.875rem',
          marginBottom: '1rem'
        }}>
          PNG, JPG, GIF, WebP, or SVG (max 5MB)
        </div>
        
        <button
          className="btn btn-outline"
          style={{ 
            pointerEvents: 'none',
            opacity: isUploading ? 0.6 : 1
          }}
          disabled={isUploading}
        >
          {isUploading ? 'Uploading...' : 'Choose File'}
        </button>
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div style={{ 
          textAlign: 'center',
          color: isDarkMode ? '#aaa' : '#666',
          fontSize: '0.875rem'
        }}>
          <div style={{ 
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: `2px solid ${isDarkMode ? '#555' : '#ddd'}`,
            borderTop: `2px solid #007bff`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginRight: '0.5rem'
          }} />
          Uploading and processing icon...
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IconUpload;
